import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { keys } from '../../api/keys'

/**
 * How long a resent email code lives. `resendEmailCode` returns nothing, so the
 * sheet uses the backend's own figure (`EMAIL_OTP_EXPIRY_MINUTES` = 10,
 * `mfa_service.py`) rather than the response's `expiresInSeconds`.
 */
export const EMAIL_CODE_LIFETIME_SECONDS = 600

/**
 * The client-side record of when the last email code sent in this tab stops
 * working — a time in ms, never the code. The backend reports no pending code,
 * so this is the only way the sheet knows a refused send left a usable one
 * behind. The entry lives in the query cache (never fetched), so sign-out and
 * user switch wipe it with `clear()`, and a reload empties it. `gcTime:
 * Infinity` because nothing observes the key and the default 5 minutes would
 * evict it while the code is still good.
 */
export function useEmailCodeExpiry() {
  const queryClient = useQueryClient()
  const key = keys.mfa.emailCodeExpiresAt()

  const remember = useCallback(
    (expiresInSeconds: number) => {
      queryClient.setQueryDefaults(key, { gcTime: Infinity })
      queryClient.setQueryData(key, Date.now() + expiresInSeconds * 1000)
    },
    [queryClient, key],
  )
  const forget = useCallback(() => queryClient.removeQueries({ queryKey: key }), [queryClient, key])
  /** True while a code sent earlier can still be entered. Read at call time. */
  const holdsLiveCode = useCallback(() => {
    const at = queryClient.getQueryData<number>(key)
    return typeof at === 'number' && at > Date.now()
  }, [queryClient, key])

  return { remember, forget, holdsLiveCode }
}
